import { Component, OnInit, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TemplateService } from 'src/app/template/shared/template.service';
import { environment } from 'src/environments/environment';
import { DialogButton } from '../dialog-header/dialog-header.component';

export interface ImagemGaleria {
  nomeArquivo: string;
  url: string;
  urlCompleta?: string;
  selecionada?: boolean;
}

@Component({
  selector: 'app-galeria-imagens-dialog',
  templateUrl: './galeria-imagens-dialog.component.html',
  styleUrls: ['./galeria-imagens-dialog.component.css']
})
export class GaleriaImagensDialogComponent implements OnInit {
  imagens: ImagemGaleria[] = [];
  imagemSelecionada: ImagemGaleria | null = null;
  carregando = false;
  
  abaAtiva = 0; // 0 = Galeria, 1 = Upload
  arquivoSelecionado: File | null = null;
  imagemPreview: string | null = null;
  uploadEmProgresso = false;

  // Paginação
  paginaAtual = 1;
  tamanhoPagina = 8;
  totalImagens = 0;
  totalPaginas = 0;
  temMais = false;

  // Dialog header
  dialogButtons: DialogButton[] = [];
  selectedTab = 0;

  constructor(
    public dialogRef: MatDialogRef<GaleriaImagensDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private templateService: TemplateService,
    private snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    this.configurarBotoes();
    this.carregarImagens();
  }

  configurarBotoes(): void {
    this.dialogButtons = [
      {
        texto: 'Inserir',
        icone: 'check',
        cor: 'primary',
        tooltip: 'Inserir imagem selecionada',
        type: 'simple',
        action: 'inserir',
        disabled: (element: any) => this.selectedTab === 0 && this.imagemSelecionada !== null,
        aoClicar: () => this.inserirImagem()
      },
      {
        texto: 'Excluir Imagem',
        icone: 'delete',
        cor: 'warn',
        tooltip: 'Excluir imagem selecionada',
        type: 'simple',
        action: 'excluir',
        disabled: (element: any) => this.selectedTab === 0 && this.imagemSelecionada !== null,
        aoClicar: () => this.excluirImagemSelecionada()
      }
    ];
  }

  onTabChange(tabId: number): void {
    this.selectedTab = tabId;
    this.abaAtiva = tabId;
  }
  carregarImagens(acumular: boolean = false): void {
    this.carregando = true;
    this.templateService.getTodasImagens(this.paginaAtual, this.tamanhoPagina).subscribe(
      (response) => {
        if (response.body) {
          const resultado = response.body;
          
          const novasImagens = resultado.imagens.map((img: any) => ({
            nomeArquivo: img.nomeArquivo,
            url: img.url,
            urlCompleta: `${environment.webApiSaguiControlUrl}${img.url}`,
            selecionada: false
          }));
          
          if (acumular) {
            this.imagens = [...this.imagens, ...novasImagens];
          } else {
            this.imagens = novasImagens;
          }
          
          this.totalImagens = resultado.totalImagens;
          this.totalPaginas = resultado.totalPaginas;
          this.temMais = resultado.temMais;
        }
        this.carregando = false;
      },
      (error) => {
        console.error('Erro ao carregar imagens:', error);
        this.snackBar.open('Erro ao carregar imagens', 'Fechar', { duration: 3000 });
        this.carregando = false;
      }
    );
  }

  carregarMais(): void {
    if (this.temMais && !this.carregando) {
      this.paginaAtual++;
      this.carregarImagens(true);
    }
  }

  selecionarImagem(imagem: ImagemGaleria): void {
    if (this.imagemSelecionada?.nomeArquivo === imagem.nomeArquivo) {
      imagem.selecionada = false;
      this.imagemSelecionada = null;
      return;
    }
    
    this.imagens.forEach(img => img.selecionada = false);
    
    imagem.selecionada = true;
    this.imagemSelecionada = imagem;
  }

  excluirImagem(imagem: ImagemGaleria, event: Event): void {
    event.stopPropagation(); 
    
    if (!confirm('Tem certeza que deseja excluir esta imagem?')) {
      return;
    }

    this.carregando = true;
    this.templateService.deleteImagem(imagem.nomeArquivo).subscribe(
      () => {
        this.snackBar.open('Imagem excluída com sucesso', 'Fechar', { duration: 3000 });
        
        if (this.imagemSelecionada?.nomeArquivo === imagem.nomeArquivo) {
          this.imagemSelecionada = null;
        }
        
        this.paginaAtual = 1;
        this.carregarImagens(false);
      },
      (error) => {
        console.error('Erro ao excluir imagem:', error);
        this.snackBar.open('Erro ao excluir imagem', 'Fechar', { duration: 3000 });
        this.carregando = false;
      }
    );
  }

  excluirImagemSelecionada(): void {
    if (!this.imagemSelecionada) {
      return;
    }
    
    if (!confirm('Tem certeza que deseja excluir esta imagem?')) {
      return;
    }

    this.carregando = true;
    this.templateService.deleteImagem(this.imagemSelecionada.nomeArquivo).subscribe(
      () => {
        this.snackBar.open('Imagem excluída com sucesso', 'Fechar', { duration: 3000 });
        this.imagemSelecionada = null;
        
        this.paginaAtual = 1;
        this.carregarImagens(false);
      },
      (error) => {
        console.error('Erro ao excluir imagem:', error);
        this.snackBar.open('Erro ao excluir imagem', 'Fechar', { duration: 3000 });
        this.carregando = false;
      }
    );
  }

  onFileSelected(event: any): void {
    const file = event.target.files[0];
    if (file) {
      const tamanhoMaximo = 1 * 1024 * 1024;
      if (file.size > tamanhoMaximo) {
        this.snackBar.open('A imagem é maior que o tamanho máximo permitido de 1MB', 'Fechar', { duration: 5000 });
        event.target.value = ''; 
        return;
      }

      this.arquivoSelecionado = file;
      
      // Criar preview
      const reader = new FileReader();
      reader.onload = (e: any) => {
        this.imagemPreview = e.target.result;
      };
      reader.readAsDataURL(file);
    }
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    
    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      const file = files[0];
      
      const tamanhoMaximo = 1 * 1024 * 1024;
      if (file.size > tamanhoMaximo) {
        this.snackBar.open('A imagem é maior que o tamanho máximo permitido de 1MB', 'Fechar', { duration: 5000 });
        return;
      }

      this.arquivoSelecionado = file;
      
      // Criar preview
      const reader = new FileReader();
      reader.onload = (e: any) => {
        this.imagemPreview = e.target.result;
      };
      reader.readAsDataURL(file);
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  uploadImagem(): void {
    if (!this.arquivoSelecionado) {
      this.snackBar.open('Selecione um arquivo', 'Fechar', { duration: 3000 });
      return;
    }

    this.uploadEmProgresso = true;
    this.templateService.uploadImagemTemplate(this.arquivoSelecionado).subscribe(
      (response) => {
        this.snackBar.open('Upload realizado com sucesso', 'Fechar', { duration: 3000 });
        
        const urlImagem = response.body.url;
        const nomeArquivoUploadado = urlImagem.split('/').pop();
        
        // Limpar upload
        this.arquivoSelecionado = null;
        this.imagemPreview = null;
        this.uploadEmProgresso = false;
        
        this.abaAtiva = 0;
        this.selectedTab = 0;
        
        this.paginaAtual = 1;
        this.carregando = true;
        this.templateService.getTodasImagens(this.paginaAtual, this.tamanhoPagina).subscribe(
          (responseGaleria) => {
            if (responseGaleria.body) {
              const resultado = responseGaleria.body;
              
              const novasImagens = resultado.imagens.map((img: any) => ({
                nomeArquivo: img.nomeArquivo,
                url: img.url,
                urlCompleta: `${environment.webApiSaguiControlUrl}${img.url}`,
                selecionada: false
              }));
              
              this.imagens = novasImagens;
              this.totalImagens = resultado.totalImagens;
              this.totalPaginas = resultado.totalPaginas;
              this.temMais = resultado.temMais;
              
              const imagemUploadada = this.imagens.find(img => img.nomeArquivo === nomeArquivoUploadado);
              if (imagemUploadada) {
                imagemUploadada.selecionada = true;
                this.imagemSelecionada = imagemUploadada;
              }
            }
            this.carregando = false;
          },
          (error) => {
            console.error('Erro ao recarregar galeria:', error);
            this.carregando = false;
          }
        );
      },
      (error) => {
        console.error('Erro ao fazer upload:', error);
        this.uploadEmProgresso = false;
      }
    );
  }

  inserirImagem(): void {
    if (!this.imagemSelecionada) {
      this.snackBar.open('Selecione uma imagem', 'Fechar', { duration: 3000 });
      return;
    }

    this.dialogRef.close({
      url: this.imagemSelecionada.urlCompleta,
      nomeArquivo: this.imagemSelecionada.nomeArquivo
    });
  }

  fechar(): void {
    this.dialogRef.close();
  }

  get podeInserir(): boolean {
    return this.imagemSelecionada !== null;
  }
}